import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Building2, ChevronRight, Loader2 } from 'lucide-react';

/**
 * LocationBreadcrumb — top-of-page switcher for multi-location stocktakes.
 *
 * Renders a breadcrumb-style row like:
 *
 *   Parent · Cellar  ›  **Main Bar** (current)  ›  Roof Bar  ›  …
 *
 * Each segment is a clickable button that navigates to that location's
 * child session. The current session is bold + non-clickable.
 *
 * Visible only when the loaded `session` is a child of a multi-location
 * audit. For single-site sessions or the parent itself it renders nothing.
 *
 * iter436 — added per user request: "when in multi-location sites, there
 * is no easy way to navigate between different locations when inside any
 * one location."
 */
export const LocationBreadcrumb = ({ session }) => {
  const navigate = useNavigate();
  const [children, setChildren] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const parentId = session?.parent_session_id;
  const currentChildId = session?.id;

  useEffect(() => {
    if (!parentId) {
      setChildren(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/stocktake/multi-location/sessions/${parentId}/children`)
      .then((res) => {
        if (cancelled) return;
        setChildren(Array.isArray(res.data) ? res.data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || 'Failed to load locations');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [parentId]);

  // Hide for non-child sessions.
  if (!parentId) return null;
  // While the API call is in flight, render a minimal skeleton so the
  // header doesn't reflow when the data arrives.
  if (loading || children === null) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-1" data-testid="location-breadcrumb-loading">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading locations…
      </div>
    );
  }
  if (error || children.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 rounded-md bg-indigo-50/60 border border-indigo-100 px-3 py-2"
      data-testid="location-breadcrumb"
    >
      <Building2 className="h-3.5 w-3.5 text-indigo-700 shrink-0" />
      <button
        type="button"
        onClick={() => navigate(`/stocktaking/sessions/${parentId}`)}
        className="text-xs text-indigo-700 hover:underline font-medium"
        data-testid="location-breadcrumb-parent"
      >
        Parent audit
      </button>
      {children.map((c) => {
        const isCurrent = c.id === currentChildId;
        const label = c.location_name || c.session_reference || `Location #${c.audit_location_id}`;
        return (
          <React.Fragment key={c.id}>
            <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />
            {isCurrent ? (
              <span
                className="text-xs font-semibold text-slate-900 bg-white border border-indigo-200 rounded px-2 py-0.5"
                data-testid={`location-breadcrumb-current-${c.audit_location_id || c.id}`}
              >
                {label}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => navigate(`/stocktaking/sessions/${c.id}`)}
                className="text-xs text-indigo-700 hover:underline hover:bg-white rounded px-2 py-0.5 transition-colors"
                data-testid={`location-breadcrumb-link-${c.audit_location_id || c.id}`}
              >
                {label}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default LocationBreadcrumb;
